import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AdminApi } from "../api/admin-api";
import AdminLayout from "../components/AdminLayout";
import { toast } from "sonner";

interface SyncResult {
  totalUsers: number;
  successfulUsers: number;
  failedUsers: number;
  errors: string[];
  lastProcessedId?: number;
}

interface BatchProgress {
  currentBatch: number;
  totalBatches: number;
  processedUsers: number;
  totalUsers: number;
  isRunning: boolean;
  accumulatedResults: SyncResult;
}

export default function IntercomSync() {
  const [lastSyncResult, setLastSyncResult] = useState<SyncResult | null>(null);
  const [batchSize, setBatchSize] = useState(50);
  const [batchProgress, setBatchProgress] = useState<BatchProgress | null>(
    null,
  );

  // Query to get user range info
  const { data: userRangeInfo } = useQuery({
    queryKey: ["userRangeInfo"],
    queryFn: AdminApi.getUserRangeInfo,
  });

  // Batch processing function
  const processBatches = async (
    userStart: number,
    userEnd: number,
    batchSizeValue: number,
  ) => {
    const totalUsers = userEnd - userStart + 1;
    const totalBatches = Math.ceil(totalUsers / batchSizeValue);
    let processedUsers = 0;

    const accumulatedResults: SyncResult = {
      totalUsers: 0,
      successfulUsers: 0,
      failedUsers: 0,
      errors: [],
    };

    setBatchProgress({
      currentBatch: 0,
      totalBatches,
      processedUsers: 0,
      totalUsers,
      isRunning: true,
      accumulatedResults,
    });

    for (let batch = 0; batch < totalBatches; batch++) {
      const batchStart = userStart + batch * batchSizeValue;
      const batchEnd = Math.min(
        userStart + (batch + 1) * batchSizeValue - 1,
        userEnd,
      );

      try {
        const result = await AdminApi.triggerIntercomSync({
          startId: batchStart,
          endId: batchEnd,
          limit: batchSizeValue,
        });

        // Accumulate results
        accumulatedResults.totalUsers += result.totalUsers;
        accumulatedResults.successfulUsers += result.successfulUsers;
        accumulatedResults.failedUsers += result.failedUsers;
        accumulatedResults.errors.push(...result.errors);

        processedUsers += result.totalUsers;

        setBatchProgress({
          currentBatch: batch + 1,
          totalBatches,
          processedUsers,
          totalUsers,
          isRunning: true,
          accumulatedResults,
        });
      } catch (error) {
        const errorMessage = `Batch ${batch + 1} failed: ${error instanceof Error ? error.message : String(error)}`;
        accumulatedResults.errors.push(errorMessage);
        toast.error(errorMessage);
        break;
      }
    }

    setBatchProgress((prev) => (prev ? { ...prev, isRunning: false } : null));
    setLastSyncResult(accumulatedResults);

    if (accumulatedResults.failedUsers === 0) {
      toast.success(
        `Successfully synced all ${accumulatedResults.successfulUsers} users in ${totalBatches} batches`,
      );
    } else {
      toast.warning(
        `Completed batched sync: ${accumulatedResults.successfulUsers}/${accumulatedResults.totalUsers} users synced`,
      );
    }
  };

  const handleSyncAll = () => {
    if (!userRangeInfo) {
      toast.error("User range info not available");
      return;
    }

    if (
      confirm(
        `This will sync all ${userRangeInfo.totalUsers} users in batches of ${batchSize}. Continue?`,
      )
    ) {
      processBatches(
        userRangeInfo.minUserId,
        userRangeInfo.maxUserId,
        batchSize,
      );
    }
  };

  const isLoading = batchProgress?.isRunning ?? false;

  return (
    <AdminLayout
      title="Intercom Sync"
      description="Manually trigger recalculation of custom attributes for all users in Intercom"
      breadcrumb="Intercom Sync"
    >
      <div className="space-y-6">
        {/* Batch Configuration */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Batch Size
              </label>
              <select
                value={batchSize}
                onChange={(e) => setBatchSize(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value={10}>10 users</option>
                <option value={25}>25 users</option>
                <option value={50}>50 users</option>
                <option value={100}>100 users</option>
              </select>
            </div>

            <button
              onClick={handleSyncAll}
              disabled={isLoading || !userRangeInfo}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                isLoading || !userRangeInfo
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
            >
              Sync All Users
            </button>
          </div>
        </div>

        {/* Batch Progress */}
        {batchProgress && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Batch Progress
            </h2>

            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Batch {batchProgress.currentBatch} of{" "}
                  {batchProgress.totalBatches}
                </span>
                <span className="text-sm text-gray-600">
                  {batchProgress.processedUsers} / {batchProgress.totalUsers}{" "}
                  users
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.min((batchProgress.processedUsers / batchProgress.totalUsers) * 100, 100)}%`,
                  }}
                ></div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-lg font-bold text-gray-600">
                  {batchProgress.accumulatedResults.totalUsers}
                </div>
                <div className="text-xs text-gray-600">Total Processed</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-lg font-bold text-green-600">
                  {batchProgress.accumulatedResults.successfulUsers}
                </div>
                <div className="text-xs text-gray-600">Successful</div>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <div className="text-lg font-bold text-red-600">
                  {batchProgress.accumulatedResults.failedUsers}
                </div>
                <div className="text-xs text-gray-600">Failed</div>
              </div>
            </div>

            {batchProgress.isRunning && (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                <span className="text-sm text-blue-600">
                  Processing batch {batchProgress.currentBatch}...
                </span>
              </div>
            )}
          </div>
        )}

        {/* Results */}
        {lastSyncResult && !isLoading && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Last Sync Results
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-gray-600">
                  {lastSyncResult.totalUsers}
                </div>
                <div className="text-sm text-gray-600">Total Users</div>
              </div>
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-green-600">
                  {lastSyncResult.successfulUsers}
                </div>
                <div className="text-sm text-gray-600">Successful</div>
              </div>
              <div className="p-4 bg-red-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-red-600">
                  {lastSyncResult.failedUsers}
                </div>
                <div className="text-sm text-gray-600">Failed</div>
              </div>
            </div>

            {/* Success Message */}
            {lastSyncResult.failedUsers === 0 && (
              <div className="p-4 bg-green-50 border-l-4 border-green-400 rounded-r-lg">
                <div className="flex">
                  <div className="text-green-400 text-xl mr-3">✅</div>
                  <div>
                    <p className="text-green-700 font-medium">
                      Sync Completed Successfully
                    </p>
                    <p className="text-green-600 text-sm mt-1">
                      All {lastSyncResult.successfulUsers} users were
                      successfully synced to Intercom.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Errors */}
            {lastSyncResult.errors.length > 0 && (
              <div className="p-4 bg-red-50 border-l-4 border-red-400 rounded-r-lg">
                <div className="flex">
                  <div className="text-red-400 text-xl mr-3">❌</div>
                  <div className="flex-1">
                    <p className="text-red-700 font-medium">Sync Errors</p>
                    <p className="text-red-600 text-sm mt-1 mb-2">
                      {lastSyncResult.failedUsers} users failed to sync:
                    </p>
                    <div className="max-h-48 overflow-y-auto">
                      <ul className="text-red-600 text-sm space-y-1">
                        {lastSyncResult.errors.map((error, index) => (
                          <li
                            key={index}
                            className="font-mono bg-red-100 p-2 rounded"
                          >
                            {error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
